import {
  Market,
  NormalizedEntity,
  SportEvent
} from "@fresh8/types/src/betData";
import { enabledParlaySizes } from "@fresh8/types/src/featureFlags";
import { Sport, SportEnum } from "@fresh8/types/src/language";
import {
  FULL_TIME_RESULT,
  MATCH_WINNER
} from "../../../../constants/market-types";
import { MarketCard, EventGroup, CreationContext } from "../../types";

const generateWdwParlayReducer = (
  markets: Record<string, Market>
): ((accumulator: string[], currentValue: SportEvent) => string[]) => {
  return (accumulator: string[], currentValue: SportEvent) => {
    const matchedMarket = currentValue.markets.find(marketId => {
      const currentMarketType = markets[marketId].marketType;

      return currentMarketType === FULL_TIME_RESULT;
    });

    if (matchedMarket) {
      accumulator.push(matchedMarket);
    }

    return accumulator;
  };
};

const generateWwParlayReducer = (
  markets: Record<string, Market>
): ((accumulator: string[], currentValue: SportEvent) => string[]) => {
  return (accumulator: string[], currentValue: SportEvent) => {
    const matchedMarket = currentValue.markets.find(marketId => {
      const currentMarketType = markets[marketId].marketType;

      return currentMarketType === MATCH_WINNER;
    });

    if (matchedMarket) {
      accumulator.push(matchedMarket);
    }

    return accumulator;
  };
};

const getAllSportsFromEvents = (
  events: NormalizedEntity<SportEvent>
): Sport[] | [] => {
  return events ? Object.values(events).map(event => event.sport) : [];
};

const getEventCountPerSport = (
  allSportsFromEvents: Sport[]
): Record<string, number> => {
  // We count how many events per sport we have
  // Remember we get 4 events in total per AdTag

  return allSportsFromEvents.reduce((acc, value) => {
    acc[value] = acc[value] += 1 || 1;

    return acc;
  }, {});
};

const getShouldCheckSport = (
  events: NormalizedEntity<SportEvent>,
  markets: Record<string, Market>
): boolean => {
  let shouldCheckSport = false;
  const NUM_OF_MARKETS_TO_MATCH = 2;

  // We wanna see whether any match has those two markets
  // and if so attach some additional logic later on
  events &&
    Object.values(events).forEach(event => {
      let numOfMatchedMarkets = 0;

      event.markets.forEach(market => {
        if (
          markets[market].marketType === FULL_TIME_RESULT ||
          markets[market].marketType === MATCH_WINNER
        ) {
          numOfMatchedMarkets++;
        }
      });

      if (numOfMatchedMarkets === NUM_OF_MARKETS_TO_MATCH) {
        shouldCheckSport = true;
      }
    });

  return shouldCheckSport;
};

const getParlayReducerGenerator = (
  events: NormalizedEntity<SportEvent>,
  markets: Record<string, Market>,
  eventCountPerSport: Record<string, number>
): ((
  markets: Record<string, Market>
) => (accumulator: string[], currentValue: SportEvent) => string[]) => {
  const shouldCheckSport = getShouldCheckSport(events, markets);
  let generateParlayReducer = generateWdwParlayReducer;
  const TOO_MANY_SPORTS_FOR_PARLAY_CARD = 3;
  const NUM_OF_MATCHES_PARLAY_THRESHOLD = 2;

  if (
    shouldCheckSport &&
    Object.values(eventCountPerSport).length < TOO_MANY_SPORTS_FOR_PARLAY_CARD
  ) {
    // We have at least one event that satisfies shouldCheckSport
    // and there are not too many different sports
    for (const sport in eventCountPerSport) {
      // Can we even show the Parlay Card for this sport?
      if (eventCountPerSport[sport] > NUM_OF_MATCHES_PARLAY_THRESHOLD) {
        // We finally check the sport and decide which reducer to return
        if (sport === SportEnum.FOOTBALL) {
          generateParlayReducer = generateWdwParlayReducer;
        } else {
          generateParlayReducer = generateWwParlayReducer;
        }
      }
    }
  } else {
    let numOfMatchesWithWdw = 0;
    let numOfMatchesWithWw = 0;

    // We do not have at least one event that satisfies shouldCheckSport
    // and we check to see which market is more present and try to use the corresponding reducer

    Object.values(events).forEach(event => {
      event.markets.forEach(market => {
        if (markets[market].marketType === FULL_TIME_RESULT) {
          numOfMatchesWithWdw++;
        }
        if (markets[market].marketType === MATCH_WINNER) {
          numOfMatchesWithWw++;
        }
      });
    });

    if (numOfMatchesWithWdw >= numOfMatchesWithWw) {
      generateParlayReducer = generateWdwParlayReducer;
    } else {
      generateParlayReducer = generateWwParlayReducer;
    }
  }

  return generateParlayReducer;
};

interface parlayChecksType {
  canShowParlayCard: boolean;
  generateParlayReducer: (
    markets: Record<string, Market>
  ) => (accumulator: string[], currentValue: SportEvent) => string[];
}

const parlayChecks = (
  events: NormalizedEntity<SportEvent>,
  markets: Record<string, Market>
): parlayChecksType => {
  const eventCountPerSport = getEventCountPerSport(
    getAllSportsFromEvents(events)
  );

  const canShowParlayCard = Object.values(eventCountPerSport).length !== 0;

  const generateParlayReducer = getParlayReducerGenerator(
    events,
    markets,
    eventCountPerSport
  );

  return {
    canShowParlayCard,
    generateParlayReducer
  };
};

export function createParlayCard(
  _: EventGroup[],
  context: CreationContext
): Array<MarketCard> {
  const { betData } = context;

  if (!enabledParlaySizes.includes(context.size.name.toLocaleLowerCase()))
    return [];

  // DEV NOTE: total number of events per AdTag is 4
  // The further code restrictions and conditionals take this into account
  const NUM_OF_EVENTS_FOR_PARLAY_CARD = 3;
  const { canShowParlayCard, generateParlayReducer } = parlayChecks(
    betData?.events ?? {},
    betData?.markets ?? {}
  );

  if (!canShowParlayCard) return [];

  const parlayMarkets = Object.values(betData.events)
    .reduce(generateParlayReducer(betData.markets), [])
    .slice(0, NUM_OF_EVENTS_FOR_PARLAY_CARD);

  if (!parlayMarkets || parlayMarkets.length < NUM_OF_EVENTS_FOR_PARLAY_CARD)
    return [];

  const parlaycard = new MarketCard({
    type: "parlay",
    eventId: "PARLAY",
    marketIds: parlayMarkets,
    optionIds: null
  });

  return [parlaycard];
}
